import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { authService } from '@/services/authService'
import { AUTH_PANEL_BG, AUTH_CARD_BG, fieldLabelSx, fieldInputSx, primaryBtnSx } from './authStyles'

const schema = z.object({
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
})

type FormData = z.infer<typeof schema>

const OrigineiLogo: FC<{ size?: number; offsetLeft?: number }> = ({ size = 64, offsetLeft = 0 }) => (
  <img
    src="/logo.png"
    alt="Originei"
    style={{
      height: size,
      width: 'auto',
      display: 'block',
      marginLeft: offsetLeft ? `-${offsetLeft}px` : undefined,
    }}
  />
)

export const ForgotPasswordPage: FC = () => {
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setError(null)
    setIsLoading(true)
    try {
      await authService.forgotPassword({ email: data.email })
      setSubmittedEmail(data.email)
      setSubmitted(true)
    } catch {
      // O backend SEMPRE retorna 200 para forgot-password (não vaza enumeração).
      // Só caímos aqui se houve erro de rede/servidor.
      setError('Não foi possível processar a solicitação. Tente novamente em alguns minutos.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: AUTH_PANEL_BG,
        padding: { xs: '32px 20px', sm: 6 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          backgroundColor: AUTH_CARD_BG,
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4), 0 16px 48px rgba(0,0,0,0.45)',
          padding: { xs: '32px 24px', sm: '40px 44px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 3 }}>
          <OrigineiLogo />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.75,
              color: '#9CA3AF',
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Plataforma de Consultores
          </Typography>
        </Box>

        {submitted ? (
          <Box>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '14px',
                backgroundColor: 'rgba(255, 98, 12,0.10)',
                color: '#ff620c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '-0.025em',
                mb: 1,
              }}
            >
              Verifique seu email
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.9375rem',
                lineHeight: 1.55,
                letterSpacing: '-0.01em',
                mb: 3,
              }}
            >
              Se{' '}
              <Box component="span" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {submittedEmail}
              </Box>{' '}
              estiver cadastrado, enviamos um link para você redefinir sua senha.
              O link expira em 1 hora.
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: '#9CA3AF',
                letterSpacing: '-0.01em',
                mb: 3,
              }}
            >
              Não recebeu? Confira a caixa de spam ou tente novamente em alguns minutos.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              sx={{
                height: 48,
                borderRadius: '10px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                textTransform: 'none',
                color: '#FFFFFF',
                borderColor: 'rgba(255,255,255,0.18)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.35)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              Voltar para o login
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3.5 }}>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  letterSpacing: '-0.025em',
                  mb: 0.75,
                }}
              >
                Esqueci minha senha
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.5,
                  letterSpacing: '-0.01em',
                }}
              >
                Informe seu email cadastrado e enviaremos um link para você criar uma nova senha.
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  border: '1px solid rgba(231,76,60,0.2)',
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  component="label"
                  htmlFor="email"
                  sx={fieldLabelSx}
                >
                  E-mail
                </Typography>
                <TextField
                  id="email"
                  {...register('email')}
                  type="email"
                  fullWidth
                  placeholder="voce@empresa.com.br"
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    sx: fieldInputSx,
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={primaryBtnSx}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} color="inherit" />
                    Enviando...
                  </Box>
                ) : (
                  'Enviar link de redefinição'
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  underline="hover"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { color: '#FFFFFF' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 14 }} />
                  Voltar para o login
                </Link>
              </Box>
            </form>
          </>
        )}
      </Box>
    </Box>
  )
}

export default ForgotPasswordPage
